<!-- Форма заказ-наряда ремонта: машина, строки работ/запчастей и optional-расход. -->
<template>
  <base-loader v-if="dataIsLoading" />

  <form
    v-else
    :id="formId"
    class="repair-form"
    @submit.prevent="handleSubmit"
  >
    <!-- Секции держат разметку полей, форма остается владельцем данных и submit -->
    <FleetRepairMainSection
      :repair="repairData"
      @status-changed="handleStatusChanged"
      :errors="errors"
      :form-data="formData"
      :status-options="statusOptions"
      @clear-error="clearBaseError"
      @open-counterparty-modal="showCounterpartyModal = true"
      @update-field="updateFormField"
    />

    <FleetRepairDescriptionSection
      :form-data="formData"
      @update-field="updateFormField"
    />

    <BaseFormSection title="Файлы" icon="download">
      <BaseFormField wide>
        <AttachmentUploader
          v-model:attachment-ids="formData.attachment_ids"
          requisite-code="fleet_repair"
          :form-id="formId"
          :existing-attachments="existingAttachments"
        />
      </BaseFormField>
    </BaseFormSection>

    <FleetRepairInspectionSection
      v-if="repairId"
      :repair-data="repairData"
      @open-inspection="showInspectionModal = true"
    />

    <FleetRepairItemsSection
      :base-amount="baseAmount"
      :format-money="formatMoney"
      :item-errors="itemErrors"
      :item-type-options="itemTypeOptions"
      :items="repairItems"
      :labor-amount="laborAmount"
      :parts-amount="partsAmount"
      :total-amount="totalAmount"
      :vat-amount="vatAmount"
      :vat-calculation-type-options="vatCalculationTypeOptions"
      :vat-kind-options="vatKindOptions"
      @add-item="addItem"
      @clear-item-error="clearItemError"
      @remove-item="removeItem"
      @sync-item-amount="syncItemAmount"
      @update-item-field="updateItemField"
    />

    <BaseFormSection title="Связанные пометки" icon="comment" :columns="1">
      <FleetRepairRemarkSelect v-model="formData.remark_ids" resource="remarks" :vehicle-id="formData.vehicle_id"
        :initial-records="repairData?.remarks || []" :disabled="isSubmitting" />
    </BaseFormSection>

    <FleetRepairPaymentSection v-model="formData.expense_ids" :vehicle-id="formData.vehicle_id"
      :repair-id="repairId" :expenses="repairData?.expenses || []" :disabled="isSubmitting" />

    <RecordListStatusHistory
      v-if="repairData"
      :key="repairData.id"
      :transitions="repairData.status_transitions || []"
      :statuses="statusOptions"
    />
  </form>

  <Teleport to="body">
    <FleetRepairInspectionModal
      v-if="showInspectionModal && repairId"
      :repair-id="repairId"
      @close="showInspectionModal = false"
      @confirmed="handleInspectionConfirmed"
    />

    <AddCounterpartyModal
      v-if="showCounterpartyModal"
      @counterparty-created="handleCounterpartyCreated"
      @close="showCounterpartyModal = false"
    />
  </Teleport>
</template>

<script>
import FleetRepairRemarkSelect from "@/components/Fleet/RepairRemark/Select.vue";
import {
  appendAssociationList,
  associationListForControl,
  mapAssociationList,
  removeAssociationList,
} from "@/utils/associationListUtils";
import { mapState } from "pinia";
import RecordListStatusHistory from "@/components/RecordList/StatusHistory.vue";

import AttachmentUploader from "@/components/Attachment/Uploader.vue";
import AddCounterpartyModal from "@/components/Counterparty/AddCounterpartyModal.vue";
import FleetRepairInspectionModal from "@/components/Fleet/Repair/InspectionModal.vue";
import FleetRepairDescriptionSection from "@/components/Fleet/Repair/sections/DescriptionSection.vue";
import FleetRepairInspectionSection from "@/components/Fleet/Repair/sections/InspectionSection.vue";
import FleetRepairItemsSection from "@/components/Fleet/Repair/sections/ItemsSection.vue";
import FleetRepairMainSection from "@/components/Fleet/Repair/sections/MainSection.vue";
import FleetRepairPaymentSection from "@/components/Fleet/Repair/sections/PaymentSection.vue";

import { useCounterpartyStore } from "@/stores/counterpartyStore";
import { useFleetRepairStore } from "@/stores/fleetRepairStore";
import { useNotificationStore } from "@/stores/notificationStore";

import {
  currentDatetime,
  formatDateTimeLocal,
  parseDateTimeLocal,
} from "@/utils/dateUtils";
import { formatBigNumber } from "@/utils/numberUtils";
import { getUserId } from "@/utils/userUtils";
import {
  payloadVatRate,
  vatRateIsInvalid,
} from "@/utils/vatUtils";
import { updateRepairItemField } from "@/utils/repairItemUtils";

export default {
  name: "FleetRepairForm",

  components: {
    FleetRepairRemarkSelect,
    RecordListStatusHistory,
    AddCounterpartyModal,
    AttachmentUploader,
    FleetRepairInspectionModal,
    FleetRepairDescriptionSection,
    FleetRepairInspectionSection,
    FleetRepairItemsSection,
    FleetRepairMainSection,
    FleetRepairPaymentSection,
  },

  props: {
    vehicleId: { type: [String, Number], default: null },
    formId: {
      type: String,
      default: "fleet-repair-form",
    },
    repairId: {
      type: Number,
      default: null,
    },
  },

  emits: ["close", "saved", "loaded", "submitting-change"],

  data() {
    return {
      formData: this.blankFormData(),
      repairData: null,
      existingAttachments: [],
      showCounterpartyModal: false,
      showInspectionModal: false,
      errors: {
        vehicle_id: false,
        responsible_id: false,
        performed_at: false,
      },
      itemsErrors: [],
    };
  },

  computed: {
    ...mapState(useFleetRepairStore, [
      "dataIsLoading",
      "isSubmitting",
      "statusOptions",
      "itemTypeOptions",
      "vatKindOptions",
      "vatCalculationTypeOptions",
    ]),

    store() {
      return useFleetRepairStore();
    },

    notificationStore() {
      return useNotificationStore();
    },

    counterpartyStore() {
      return useCounterpartyStore();
    },

    repairItems() {
      return associationListForControl(this.formData.items);
    },

    laborAmount() {
      return this.repairItems
        .filter((item) => item.item_type === "labor")
        .reduce((sum, item) => sum + this.itemTotalAmount(item), 0);
    },

    partsAmount() {
      return this.repairItems
        .filter((item) =>
          ["part", "material"].includes(item.item_type)
        )
        .reduce((sum, item) => sum + this.itemTotalAmount(item), 0);
    },

    baseAmount() {
      return this.repairItems.reduce(
        (sum, item) => sum + this.itemBaseAmount(item),
        0
      );
    },

    vatAmount() {
      return this.repairItems.reduce(
        (sum, item) => sum + this.itemVatAmount(item),
        0
      );
    },

    totalAmount() {
      return this.repairItems.reduce(
        (sum, item) => sum + this.itemTotalAmount(item),
        0
      );
    },
  },

  watch: {
    isSubmitting(value) {
      this.$emit("submitting-change", value);
    },
  },

  methods: {
    blankFormData() {
      return {
        vehicle_id: this.vehicleId ? Number(this.vehicleId) : null,
        fleet_park_id: null,
        responsible_id: getUserId(),
        counterparty_id: null,
        performed_at: currentDatetime(),
        status: "done",
        odometer: "",
        title: "",
        description: "",
        remark_ids: [],
        expense_ids: [],
        attachment_ids: [],
        items: [this.blankItem()],
      };
    },

    blankItem(data = {}) {
      return {
        local_id: data.local_id || `${Date.now()}-${Math.random()}`,
        catalog_item_id: data.catalog_item_id || null,
        create_catalog_item: data.create_catalog_item || false,
        item_type: data.item_type || "labor",
        name: data.name || "",
        description: data.description || "",
        quantity: data.quantity || 1,
        unit: data.unit || "",
        unit_price: data.unit_price || "",
        amount: data.amount || "",
        vat_kind: data.vat_kind || "no_vat",
        vat_rate: data.vat_rate || "",
        vat_calculation_type:
          data.vat_calculation_type || "included",
      };
    },

    repairItemData(item) {
      return {
        local_id: item.id || `${Date.now()}-${Math.random()}`,
        catalog_item_id: item.catalog_item_id || null,
        create_catalog_item: false,
        item_type: item.item_type,
        name: item.name,
        description: item.description,
        quantity: item.quantity,
        unit: item.unit,
        unit_price: item.unit_price,
        amount: item.amount,
        vat_kind: item.vat_kind,
        vat_rate: item.vat_rate,
        vat_calculation_type: item.vat_calculation_type,
      };
    },

    async loadRepair() {
      if (!this.repairId) return;

      const repair = await this.store.fetchRepair(this.repairId);
      this.applyRepair(repair);
    },

    applyRepair(repair) {
      this.repairData = repair;
      this.$emit("loaded", repair);
      this.formData = {
        remark_ids: mapAssociationList(repair.remarks, (remark) => remark.id),
        expense_ids: mapAssociationList(repair.expenses, (expense) => expense.id),
        vehicle_id: repair.vehicle_id,
        fleet_park_id: repair.fleet_unit?.fleet_park_id || null,
        // При редактировании пустой ответственный должен остаться пустым до явного выбора.
        responsible_id: repair.responsible_id || null,
        counterparty_id: repair.counterparty_id || null,
        performed_at: formatDateTimeLocal(repair.performed_at),
        status: repair.status,
        odometer: repair.odometer || "",
        title: repair.title || "",
        description: repair.description || "",
        attachment_ids: mapAssociationList(repair.attachments,
          (attachment) => attachment.id
        ),
        items: mapAssociationList(repair.items, (item) =>
          this.repairItemData(item)
        ),
      };
      this.existingAttachments = repair.attachments || [];

      if (this.formData.items?.length === 0) {
        this.formData.items = [this.blankItem()];
      }

    },

    addItem(itemType = "labor") {
      this.formData.items = appendAssociationList(
        this.formData.items,
        this.blankItem({ item_type: itemType })
      );
    },

    removeItem(index) {
      this.formData.items = removeAssociationList(
        this.formData.items,
        (_item, itemIndex) => itemIndex === index
      );
      this.itemsErrors.splice(index, 1);
    },

    itemErrors(index) {
      return this.itemsErrors[index] || {};
    },

    updateItemField({ index, field, value }) {
      if (!this.formData.items[index]) return;
      updateRepairItemField(this.formData.items[index], field, value);
    },

    clearItemError(index, field) {
      if (!this.itemsErrors[index]) return;
      this.itemsErrors[index][field] = false;
    },

    handleInspectionConfirmed(repair) {
      this.repairData = repair;
      this.showInspectionModal = false;
    },

    async handleCounterpartyCreated(counterparty) {
      await this.counterpartyStore.fetchCounterparties();
      this.updateFormField("counterparty_id", counterparty.id);
      this.clearBaseError("counterparty_id");
    },

    updateFormField(field, value) {
      if (
        field === "vehicle_id" &&
        String(this.formData.vehicle_id) !== String(value)
      ) {
        this.formData.remark_ids = [];
        this.formData.expense_ids = [];
      }

      this.formData[field] = value;
    },

    clearBaseError(field) {
      if (!this.errors[field]) return;
      this.errors[field] = false;
    },

    syncItemAmount(index) {
      const item = this.formData.items[index];
      const quantity = this.numberValue(item.quantity);
      const unitPrice = this.numberValue(item.unit_price);

      if (!quantity || !unitPrice) return;
      item.amount = Math.round(quantity * unitPrice * 100) / 100;
    },

    validateForm() {
      this.errors = this.baseFormErrors();
      this.itemsErrors = this.itemFormErrors();

      const hasBaseErrors = Object.values(this.errors).some(Boolean);
      const hasItemErrors = this.itemsErrors.some((item) =>
        Object.values(item).some(Boolean)
      );
      if (hasBaseErrors || hasItemErrors) {
        this.notificationStore.showError(
          "Пожалуйста, заполните обязательные поля"
        );
      }

      return !hasBaseErrors && !hasItemErrors;
    },

    baseFormErrors() {
      return {
        vehicle_id: !this.formData.vehicle_id,
        responsible_id: !this.formData.responsible_id,
        performed_at: !this.formData.performed_at,
      };
    },

    itemFormErrors() {
      return this.repairItems.map((item) => ({
        item_type: !item.item_type,
        name: !String(item.name || "").trim(),
        vat_kind: !item.vat_kind,
        vat_rate: this.isVatRateInvalid(item),
        vat_calculation_type: !item.vat_calculation_type,
      }));
    },

    async handleSubmit() {
      if (!this.validateForm()) return;

      const savedRepair = await this.saveRepair();
      this.$emit("saved", savedRepair);
      this.$emit("close");
    },

    handleStatusChanged(repair) {
      this.repairData = repair;
      this.formData.status = repair.status;
      this.formData.remark_ids = mapAssociationList(repair.remarks, (remark) => remark.id);
    },

    async saveRepair() {
      const payload = this.buildPayload();

      return this.repairId
        ? await this.store.updateRepair(this.repairId, payload)
        : await this.store.createRepair(payload);
    },

    buildPayload() {
      const payload = {
        remark_ids: this.formData.remark_ids,
        expense_ids: this.formData.expense_ids,
        vehicle_id: this.formData.vehicle_id,
        responsible_id: this.formData.responsible_id,
        counterparty_id: this.emptyToNull(this.formData.counterparty_id),
        performed_at: parseDateTimeLocal(this.formData.performed_at),
        ...(!this.repairId ? { status: this.formData.status } : {}),
        odometer: this.emptyToNull(this.formData.odometer),
        title: this.emptyToNull(this.formData.title),
        description: this.emptyToNull(this.formData.description),
        attachment_ids: this.formData.attachment_ids,
        items: mapAssociationList(this.formData.items, (item, index) => ({
          catalog_item_id: this.payloadCatalogItemId(item),
          create_catalog_item: Boolean(item.create_catalog_item),
          item_type: item.item_type,
          name: item.name,
          description: this.emptyToNull(item.description),
          quantity: this.emptyToNull(item.quantity),
          unit: this.emptyToNull(item.unit),
          unit_price: this.emptyToNull(item.unit_price),
          amount: this.emptyToNull(item.amount),
          vat_kind: item.vat_kind,
          vat_rate: this.payloadVatRate(item),
          vat_calculation_type: item.vat_calculation_type,
          position: index + 1,
        })),
      };


      return payload;
    },

    payloadCatalogItemId(item) {
      if (item.create_catalog_item) return null;

      return this.emptyToNull(item.catalog_item_id);
    },

    emptyToNull(value) {
      if (value === "" || value === undefined) return null;
      return value;
    },

    numberValue(value) {
      if (value === "" || value === null || value === undefined) {
        return 0;
      }
      const number = Number(value);
      return Number.isNaN(number) ? 0 : number;
    },

    isVatRateInvalid(item) {
      if (vatRateIsInvalid(item.vat_kind, item.vat_rate)) return true;
      if (item.vat_kind !== "with_vat") return false;

      return Number(item.vat_rate) > 100;
    },

    payloadVatRate(item) {
      return payloadVatRate(item.vat_kind, item.vat_rate);
    },

    itemBaseAmount(item) {
      const amount = this.numberValue(item.amount);
      if (item.vat_kind !== "with_vat") return amount;
      if (item.vat_calculation_type !== "included") return amount;

      return amount / (1 + this.numberValue(item.vat_rate) / 100);
    },

    itemVatAmount(item) {
      if (item.vat_kind !== "with_vat") return 0;

      const amount = this.numberValue(item.amount);
      const rate = this.numberValue(item.vat_rate) / 100;
      if (!rate) return 0;
      if (item.vat_calculation_type === "included") {
        return amount - this.itemBaseAmount(item);
      }

      return amount * rate;
    },

    itemTotalAmount(item) {
      const amount = this.numberValue(item.amount);
      if (item.vat_kind !== "with_vat") return amount;
      if (item.vat_calculation_type === "included") return amount;

      return amount + this.itemVatAmount(item);
    },

    formatMoney(value) {
      return `${formatBigNumber(value)} ₽`;
    },
  },

  async created() {
    await this.loadRepair();
  },
};
</script>

<style scoped>
.repair-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  --form-section-margin: 0px;
}
</style>
